import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Mail, Copy, Check } from 'lucide-react';

const OTDEmailGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  
  // Get cars and zip from navigation state
  const { cars: passedCars, zipCode: passedZip } = location.state || {};
  
  const [emailData, setEmailData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    vin: '',
    zip: passedZip || ''
  });
  const [generatedEmail, setGeneratedEmail] = useState('');

  // Auto-generate email if cars were passed
  useEffect(() => {
    if (passedCars && passedCars.length > 0) {
      generateMultiCarEmail();
    }
  }, []);

  const handleBack = () => {
    // Go back to quote request if we came from there, otherwise compare-cars
    if (passedCars) {
      navigate('/tools/otd-quote-request');
    } else {
      navigate('/tools/compare-cars');
    }
  };

  const generateMultiCarEmail = () => {
    if (!passedCars || passedCars.length === 0) return;
    
    // Get zip from manual cars or passed zip
    const zipToUse = passedCars.find(car => car.zip)?.zip || passedZip || '[Your ZIP]';
    
    const carList = passedCars.map((car, idx) => {
      let line = `${idx + 1}. ${car.displayText}`;
      if (car.link) {
        line += `\n   Link/VIN: ${car.link}`;
      }
      if (car.zip && car.zip !== zipToUse) {
        line += `\n   ZIP: ${car.zip}`;
      }
      return line;
    }).join('\n');

    const email = `Subject: Written OTD (Out-the-Door) Quotes - ${passedCars.length} Vehicle${passedCars.length > 1 ? 's' : ''}

Hello,

I'm comparing a few stores today and would like written OTD (Out-the-Door) quotes for the following vehicle${passedCars.length > 1 ? 's' : ''}:

${carList}

My ZIP code: ${zipToUse}

For each vehicle you have in stock, please attach or link the buyer's order PDF showing:
• Vehicle price
• Taxes
• Title/registration
• Doc fee
• Any add-ons (each on its own line)

I use the OTD total to decide where to buy.

Thank you,
[Your Name]
[Phone #]`;

    setGeneratedEmail(email);
  };

  const generateOTDEmail = () => {
    const email = `Subject: Written OTD (Out-the-Door) Quote - ${emailData.year} ${emailData.make} ${emailData.model}${emailData.vin ? `, VIN ${emailData.vin}` : ''}

Hello,

I'm comparing a few stores today.

Please send me the itemized OTD (Out-the-Door) price for this vehicle:

• Year/Make/Model/Trim: ${emailData.year} ${emailData.make} ${emailData.model}${emailData.trim ? ` ${emailData.trim}` : ''}
${emailData.vin ? `• VIN: ${emailData.vin}` : '• Stock Link: [please provide]'}
• My ZIP: ${emailData.zip}

Please attach or link the buyer's order PDF showing: Vehicle price, taxes, title/registration, doc fee, and any add-ons (each on its own line).

I use the OTD total to decide where to buy.

Thank you,
[Your Name]
[Phone #]`;

    setGeneratedEmail(email);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <div className="bg-white border-b" style={{ borderBottomColor: '#E5E7EB' }}>
        <div className="max-w-screen-sm mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
              style={{ color: '#0B1221' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <span className="text-sm font-semibold tracking-wide">
              <span style={{ color: '#001F3F' }}>VINdicated.</span>
              <span style={{ color: '#FF4136' }}>ai</span>
            </span>
          </div>
          
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: '#001F3F' }}>
            Out-the-door email generator
          </h1>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: '#6B7280' }}>
            Generate a professional email to request written out-the-door quotes from dealers.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-sm mx-auto px-4 py-6 space-y-5">
        {/* Form Card */}
        <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Year
              </label>
              <input
                type="text"
                placeholder="2024"
                value={emailData.year}
                onChange={(e) => setEmailData({...emailData, year: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Make
              </label>
              <input
                type="text"
                placeholder="Toyota"
                value={emailData.make}
                onChange={(e) => setEmailData({...emailData, make: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Model
              </label>
              <input
                type="text"
                placeholder="Camry"
                value={emailData.model}
                onChange={(e) => setEmailData({...emailData, model: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Trim (optional)
              </label>
              <input
                type="text"
                placeholder="SE"
                value={emailData.trim}
                onChange={(e) => setEmailData({...emailData, trim: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
              VIN (optional)
            </label>
            <input
              type="text"
              placeholder="1HGBH41JXMN109186"
              value={emailData.vin}
              onChange={(e) => setEmailData({...emailData, vin: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
              Your ZIP Code
            </label>
            <input
              type="text"
              placeholder="90210"
              value={emailData.zip}
              onChange={(e) => setEmailData({...emailData, zip: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
            />
          </div>

        </div>

        {/* Generate Button */}
        <button
          onClick={generateOTDEmail}
          className="w-full py-4 px-6 rounded-xl font-bold text-lg transition-all shadow-md flex items-center justify-center gap-2"
          style={{ backgroundColor: '#001F3F', color: '#FFFFFF' }}
        >
          <Mail className="w-5 h-5" />
          <span>Generate Email</span>
        </button>

        {/* Generated Email Output */}
        {generatedEmail && (
          <div className="space-y-3">
            <div className="rounded-xl p-4" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
              <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono" style={{ color: '#374151' }}>
                {generatedEmail}
              </pre>
            </div>
            <button
              onClick={() => copyToClipboard(generatedEmail)}
              className="w-full py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: copied ? '#10B981' : '#FFFFFF', 
                color: copied ? '#FFFFFF' : '#001F3F',
                border: copied ? '1px solid #10B981' : '1px solid #001F3F'
              }}
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  <span>Copy to Clipboard</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Tip */}
        <p className="text-xs leading-relaxed" style={{ color: '#9CA3AF' }}>
          <strong>Tip:</strong> Send this email to 3-5 dealers to compare OTD quotes. The dealer with the lowest clean total wins.
        </p>
      </div>
    </div>
  );
};

export default OTDEmailGenerator;
